/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import {
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_FAILED
} from '../../../../share/util/api/polling';
import error from '../../../../common/nodejs/errors';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import util from "../../../../share/util/utils";

/**
 * 各フェーズ定義
 */
const PHASE = {
  EXECUTING: 'executing',
  SUCCESS: 'success',
  EXECUTE_FAILED: 'executeFailed',
  SAVING_FAILED: 'savingFailed'
};

export default {
  name: 'requirement135',
  data() {
    return {
      phase: PHASE.EXECUTING,
      isLoading: false,
      systemId: '',
      saveDataId: '',
      saveDate: '',
      modal_requirement_135: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.SAVING_FAILED:
        case PHASE.EXECUTE_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTING:
        default:
          return 'secondary';
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      this.modal_requirement_135 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // データ読出しの実行
      await this.executeDataRead();
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_135 = false;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.EXECUTE_FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** キャリブレーションデータ読出し */
    async executeDataRead() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response が存在しない場合、エラーに遷移
        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了
              this.phase = PHASE.SUCCESS;
              this.saveDate = util.timeConverter(response.data.user_data.data_id);
              this.saveDataId = response.data.user_data.data_id;
              break;
            case REQUEST_STATUS_FAILED:
              switch (response.data.async_code) {
                case error.WSP_135_SAVE_FAILED_ERROR:
                  // 保存失敗表示画面へ遷移
                  this.phase = PHASE.SAVING_FAILED;
                  break;
                default:
                  // 引き継ぎエラー表示画面へ遷移
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        } else {
          this.showUnexpectedError();
        }
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.init();
  }
};
